import React, { useState, useEffect, useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Type for the API response
type TokenUsageApiEntry = {
  date: string;
  model_used: string;
  count: number;
};

// Type for the transformed data we'll use in the chart
type TokenUsageByModelEntry = {
  date: string;
  [model: string]: number | string; // date is string, model values are number
};

// Define type for color mapping
type ColorMapping = {
  [key: string]: string;
};

interface TokenUsageChartProps {
  initialTokenData: TokenUsageApiEntry[]; // API response format
  colors?: ColorMapping; // Dynamic color mapping for different models
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  fromDate?: string; // Add prop to accept initial from date
  toDate?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number;
  }>;
  label?: string;
}

const TokenUsageByModelChart: React.FC<TokenUsageChartProps> = ({ 
  initialTokenData,
  colors = {
    'gpt-4o': '#4F46E5',  // Indigo for GPT-4o
    'gpt-3.5': '#10B981', // Green for GPT-3.5
    'claude-3': '#8B5CF6'  // Purple for Claude
  },
  onDateRangeChange,
  fromDate, // Receive from date from parent
  toDate
}) => {
  // Define type for default colors
  const defaultColors: ColorMapping = {
    'gpt-4o': '#6366f1',     // Indigo
    'gpt-4': '#6366f1',      // Indigo
    'gpt-3.5': '#10b981',    // Emerald
    'claude-3': '#8b5cf6',   // Violet
    'claude-4': '#a855f7',   // Purple
    'gemini': '#06b6d4',     // Cyan
    'palm': '#f59e0b',       // Amber
    'llama': '#ef4444',      // Red
    'mistral': '#ec4899',    // Pink
    'anthropic': '#84cc16',  // Lime
  };

  // Merge default colors with provided colors
  const finalColors: ColorMapping = { ...defaultColors, ...colors };

  // Get color for a model with fallback
  const getModelColor = (model: string, index: number): string => {
    return finalColors[model] || `hsl(${index * 45}, 70%, 55%)`;
  };

  // Helper function to validate date strings
  const isValidDate = (dateString: string): boolean => {
    if (!dateString || dateString.trim() === '') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayString = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function to get a default start date (7 days ago)
  const getDefaultStartDate = (): string => {
    if (initialTokenData && initialTokenData.length > 0) {
      const firstDataDate = initialTokenData[0].date;
      if (isValidDate(firstDataDate)) {
        return firstDataDate;
      }
    }
    // Fallback to 7 days ago
    const today = new Date();
    today.setDate(today.getDate() - 6);
    return today.toISOString().split('T')[0];
  };

  // Transform API data format to the chart format
  const transformApiData = (apiData: TokenUsageApiEntry[]): TokenUsageByModelEntry[] => {
    if (!apiData || apiData.length === 0) return [];

    // Create a map to organize data by date
    const dataByDate: Record<string, { [model: string]: number }> = {};
    
    // Track all unique models we encounter
    const allModels = new Set<string>();
    
    // Group by date and aggregate counts by model
    apiData.forEach(item => {
      if (!item.date || !isValidDate(item.date)) return;
      
      // Convert API date format (YYYY-MM-DD) to MM/DD/YYYY for consistency
      const apiDate = new Date(item.date);
      const formattedDate = `${apiDate.getMonth() + 1}/${apiDate.getDate()}/${apiDate.getFullYear()}`;
      
      if (!dataByDate[formattedDate]) {
        dataByDate[formattedDate] = {};
      }
      
      // Add this model's count to the appropriate date entry
      dataByDate[formattedDate][item.model_used] = (dataByDate[formattedDate][item.model_used] || 0) + item.count;
      
      // Track this model
      allModels.add(item.model_used);
    });
    
    // Convert the grouped data to the array format expected by the chart
    return Object.entries(dataByDate).map(([date, models]) => {
      const entry: TokenUsageByModelEntry = { date };
      
      // Make sure each model has a value (even if 0) for each date
      allModels.forEach(model => {
        entry[model] = models[model] || 0;
      });
      
      return entry;
    }).sort((a, b) => {
      // Sort by date ascending
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  };

  // Transform API data format to the chart format - memoize to prevent recalculation on every render
  const allTokenData = useMemo(() => {
    return transformApiData(initialTokenData);
  }, [initialTokenData]);
  
  // Extract all unique models from the API data - memoize to prevent recalculation
  const uniqueModels = useMemo(() => {
    if (!initialTokenData || initialTokenData.length === 0) return [];
    return Array.from(new Set(initialTokenData.map(item => item.model_used)));
  }, [initialTokenData]);
  
  // Convert MM/DD/YYYY to YYYY-MM-DD for HTML date input (with validation)
  const convertToInputDateFormat = (dateString: string): string => {
    if (!dateString || dateString.trim() === '') return getTodayString();
    
    // If it's already in YYYY-MM-DD format, return as is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return isValidDate(dateString) ? dateString : getTodayString();
    }
    
    // If it's in MM/DD/YYYY format, convert it
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const converted = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      return isValidDate(converted) ? converted : getTodayString();
    }
    
    return getTodayString();
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for filtering (with validation)
  const convertToDataDateFormat = (dateString: string): string => {
    if (!dateString || dateString.trim() === '') return '';
    
    const date = new Date(dateString);
    if (!isValidDate(dateString)) return '';
    
    return `${(date.getMonth() + 1)}/${date.getDate()}/${date.getFullYear()}`;
  };

  // Calculate end date (max 7 days from start date)
  function calculateEndDate(startDateStr: string): string {
    if (!startDateStr || !isValidDate(startDateStr)) {
      return getTodayString();
    }
    
    const start = new Date(startDateStr);
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Add 6 days to get a 7-day range (inclusive)
    return end.toISOString().split('T')[0];
  }

  // Initialize state with proper default values or props
  const getInitialStartDate = (): string => {
    if (fromDate && isValidDate(fromDate)) return fromDate;
    return getDefaultStartDate();
  };

  const getInitialEndDate = (): string => {
    if (toDate && isValidDate(toDate)) return toDate;
    const startDateToUse = fromDate && isValidDate(fromDate) ? fromDate : getDefaultStartDate();
    return calculateEndDate(startDateToUse);
  };

  const [startDate, setStartDate] = useState<string>(getInitialStartDate);
  const [endDate, setEndDate] = useState<string>(getInitialEndDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<TokenUsageByModelEntry[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Handle external prop changes (fromDate/toDate)
  useEffect(() => {
    if (fromDate && isValidDate(fromDate)) {
      setStartDate(fromDate);
    }
    if (toDate && isValidDate(toDate)) {
      setEndDate(toDate);
    }
  }, [fromDate, toDate]);

  // Generate array of all dates in range
  function generateDatesInRange(start: Date, end: Date): string[] {
    const dates: string[] = [];
    const currentDate = new Date(start);
    
    while (currentDate <= end) {
      const month = currentDate.getMonth() + 1;
      const day = currentDate.getDate();
      const year = currentDate.getFullYear();
      dates.push(`${month}/${day}/${year}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  }

  // Handle start date change
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    
    if (!newStartDate || newStartDate.trim() === '') {
      setErrorMessage("Please select a valid start date");
      return;
    }
    
    if (!isValidDate(newStartDate)) {
      setErrorMessage("Invalid start date format");
      return;
    }
    
    setStartDate(newStartDate);
    
    // Calculate new end date (max 7 days from new start date)
    const newEndDate = calculateEndDate(newStartDate);
    setEndDate(newEndDate);
    setErrorMessage(null);
  };

  // Handle end date change
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    
    if (!newEndDate || newEndDate.trim() === '') {
      setErrorMessage("Please select a valid end date");
      return;
    }
    
    if (!isValidDate(newEndDate)) {
      setErrorMessage("Invalid end date format");
      return;
    }
    
    if (!isValidDate(startDate)) {
      setErrorMessage("Please select a valid start date first");
      return;
    }
    
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(newEndDate);
    
    // Calculate difference in days
    const diffTime = endDateObj.getTime() - startDateObj.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      setErrorMessage("End date cannot be before start date");
      return;
    }
    
    if (diffDays > 6) {
      setErrorMessage("Maximum date range is 7 days");
      return;
    }
    
    setEndDate(newEndDate);
    setErrorMessage(null);
  };

  // Filter data based on selected date range and ensure all dates in range are included
  useEffect(() => {
    if (!startDate || !endDate || !isValidDate(startDate) || !isValidDate(endDate)) {
      setFilteredData([]);
      return;
    }
    
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    
    // Generate all dates in the selected range
    const datesInRange = generateDatesInRange(startDateObj, endDateObj);
    
    // Create a lookup table for existing data
    const dataByDate: Record<string, TokenUsageByModelEntry> = {};
    
    allTokenData.forEach(item => {
      dataByDate[item.date] = item;
    });
    
    // Create the filtered data array with placeholders for missing dates
    const filtered = datesInRange.map(date => {
      if (dataByDate[date]) {
        return dataByDate[date];
      } else {
        // Create a placeholder entry for dates with no data
        const emptyEntry: TokenUsageByModelEntry = { date };
        
        // Add all models with zero values
        uniqueModels.forEach(model => {
          emptyEntry[model] = 0;
        });
        
        return emptyEntry;
      }
    });
    
    setFilteredData(filtered);
  }, [startDate, endDate, allTokenData, uniqueModels]);

  // Format display dates to MM/DD/YYYY (with validation)
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr || !isValidDate(dateStr)) return 'Invalid Date';
    
    const converted = convertToDataDateFormat(dateStr);
    return converted || 'Invalid Date';
  };

  // Custom tooltip component
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              <span className="font-medium">{entry.name}:</span> {entry.value?.toLocaleString() || 0} tokens
            </p>
          ))}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Total: {payload.reduce((sum, entry) => sum + (entry.value || 0), 0).toLocaleString()} tokens
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Apply date changes and trigger API fetch
  const applyDateChanges = () => {
    if (errorMessage || !isValidDate(startDate) || !isValidDate(endDate)) return;
    
    // Call the parent callback with the new dates
    if (onDateRangeChange) {
      onDateRangeChange(startDate, endDate);
    }
    
    setIsDatePickerOpen(false);
  };

  // Reset date picker to valid defaults if needed
  const resetToDefaults = () => {
    const defaultStart = getDefaultStartDate();
    const defaultEnd = calculateEndDate(defaultStart);
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setErrorMessage(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 tracking-tight">Token Usage by Model</h3>
        
        {/* Date picker section */}
        <div className="relative">
          <button 
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200 font-medium shadow-sm border border-gray-200"
          >
            <Calendar className="h-4 w-4 text-gray-600" />
            <span className="text-gray-700">
              {isValidDate(startDate) && isValidDate(endDate) 
                ? `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`
                : 'Select Date Range'
              }
            </span>
          </button>
          
          {isDatePickerOpen && (
            <div className="absolute right-0 mt-2 p-4 bg-white rounded-xl shadow-xl border border-gray-200 z-10 w-80">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={startDate || ''}
                    onChange={handleStartDateChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={endDate || ''}
                    onChange={handleEndDateChange}
                    min={startDate}
                    max={isValidDate(startDate) ? calculateEndDate(startDate) : ''}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1 font-medium">Maximum 7 days from start date</p>
                </div>
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm font-medium">
                    {errorMessage}
                  </div>
                )}
                <div className="flex justify-between gap-2 pt-2">
                  <button 
                    onClick={() => setIsDatePickerOpen(false)}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={resetToDefaults}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium shadow-sm"
                  >
                    Reset
                  </button>
                  <button 
                    onClick={applyDateChanges}
                    className={`bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium shadow-sm ${
                      errorMessage || !isValidDate(startDate) || !isValidDate(endDate) 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-indigo-700 transition-colors'
                    }`}
                    disabled={!!errorMessage || !isValidDate(startDate) || !isValidDate(endDate)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="h-80 bg-gradient-to-br from-gray-50 to-white rounded-lg p-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={filteredData}
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          >
            <defs>
              {/* Create gradient definitions dynamically based on available models */}
              {uniqueModels.map((model, index) => (
                <linearGradient key={model} id={`color${model.replace(/[-\.]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={getModelColor(model, index)} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={getModelColor(model, index)} stopOpacity={0.1}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
            <XAxis 
              dataKey="date" 
              stroke="#6b7280"
              fontSize={12}
              fontWeight="500"
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              fontWeight="500"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ 
                paddingTop: '20px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            />
            
            {/* Generate Area components dynamically based on available models */}
            {uniqueModels.map((model, index) => (
              <Area 
                key={model}
                type="monotone" 
                dataKey={model} 
                stroke={getModelColor(model, index)} 
                strokeWidth={2}
                fillOpacity={1} 
                fill={`url(#color${model.replace(/[-\.]/g, '')})`}
                name={model} 
                activeDot={{ 
                  r: 6, 
                  stroke: getModelColor(model, index),
                  strokeWidth: 2,
                  fill: 'white'
                }} 
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {filteredData.length === 0 && (
        <div className="text-center mt-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
          </div>
          <p className="text-gray-500 text-lg font-medium">No data available for the selected date range</p>
          <p className="text-gray-400 text-sm mt-1">Try selecting a different date range or check back later</p>
        </div>
      )}

      {filteredData.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Date Range: <span className="font-semibold text-gray-800">{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</span></span>
            <span>Total Models: <span className="font-semibold text-gray-800">{uniqueModels.length}</span></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenUsageByModelChart;